import React, { useMemo } from "react";
import Table from "../Table/Table";
import { GoCheck, GoEye } from "react-icons/go";

const statusPill = (name = "") => {
  const n = String(name || "").toLowerCase();
  if (n === "pendiente")
    return { bg: "bg-yellow-500", txt: "text-white", br: "border-yellow-300", dot: "bg-yellow-900" };
  if (n === "aprobada")
    return { bg: "bg-custom-green-ccard-1", txt: "text-white", br: "border-green-300", dot: "bg-green-900" };
  if (n === "rechazada")
    return { bg: "bg-red-500", txt: "text-white", br: "border-red-300", dot: "bg-red-900" };
  return { bg: "bg-gray-100", txt: "text-gray-700", br: "border-gray-300", dot: "bg-gray-500" };
};

const fmtDate = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("es-AR", { timeZone: "UTC" });
  } catch {
    return "";
  }
};

const vpnStatusLabel = (status) => {
  switch (status) {
    case "pending":
      return "Pendiente";
    case "active":
      return "Activa";
    case "expired":
      return "Finalizada";
    case "error":
      return "Error";
    case "not_approved":
      return "No aprobada";
    case "not_requested":
    default:
      return "—";
  }
};

const buildColumns = ({ onApprove, isPrivileged, onViewDetail, currentUserId }) => {
  const base = [
    { Header: "ID", accessor: "id", width: "60px" },

    {
      Header: "Fecha de Retiro",
      accessor: "withdrawalDate",
      Cell: ({ value }) => fmtDate(value),
    },
    {
      Header: "Fecha de Devolución",
      accessor: "returnDate",
      Cell: ({ value }) => fmtDate(value),
    },

    {
      Header: "Solicitante",
      accessor: "employee.screenname",
      Cell: ({ row }) => row.original?.employee?.screenname ?? "—",
    },
    {
      Header: "Aprobador",
      accessor: "approver.screenname",
      Cell: ({ row }) => row.original?.approver?.screenname ?? "—",
    },

    {
      Header: "Ítems",
      accessor: "items",
      Cell: ({ row }) => {
        const items = row.original?.items ?? [];
        if (!items.length) return "—";
        // Muestra cantidad y, en title, el listado
        const title = items.map((it) => `• ${it.description || `#${it.stockId}`}`).join("\n");
        return (
          <span title={title}>
            {items.length} {items.length === 1 ? "ítem" : "ítems"}
          </span>
        );
      },
    },

    {
      Header: "VPNs",
      accessor: "vpns",
      Cell: ({ row }) => {
        const vpns = row.original?.vpns ?? [];
        if (!vpns.length) return "—";
        return vpns.map((v) => v.name).join(", ");
      },
    },
    {
      Header: "Activación VPN",
      accessor: "vpnActivationApproved",
      Cell: ({ row }) => {
        if (row.original?.vpnActivationApproved) {
          return (
            <span title={row.original?.vpnActivationLastError || ""}>
              {vpnStatusLabel(row.original?.vpnActivationStatus)}
            </span>
          );
        }
        if (row.original?.vpnActivationRequested) return "Solicitada";
        return "—";
      },
    },

    {
      Header: "Estado",
      accessor: "status.name",
      Cell: ({ row }) => {
        const name = row.original?.status?.name ?? "Pendiente";
        const s = statusPill(name);
        return (
          <span
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${s.bg} ${s.txt} ${s.br}`}
            title={name}
          >
            <span className={`w-2 h-2 rounded-full ${s.dot}`} />
            {name}
          </span>
        );
      },
    },
    {
      Header: "Detalle",
      id: "detalle",
      Cell: ({ row }) => (
        <button
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md border bg-white hover:bg-gray-50"
          onClick={() => onViewDetail?.(row.original)}
          title="Ver detalle"
        >
          <GoEye /> Ver
        </button>
      ),
      width: "90px",
    },
  ];

  // Columna de acción sólo para Admin y cuando está Pendiente
  const actionCol = {
    id: "acciones",
    Header: "Aprobar / Rechazar",
    accessor: "actions",
    Cell: ({ row }) => {
      const name = row.original?.status?.name ?? "Pendiente";
      const isOwnRequest = Number(row.original?.employee?.id) === Number(currentUserId);
      if (!isPrivileged || isOwnRequest || String(name).toLowerCase() !== "pendiente") {
        return <span className="text-gray-400">—</span>;
      }
      return (
        <div className="flex gap-2 justify-center items-center">
          <button
            className="flex items-center gap-1 text-white bg-custom-green-ccard-1 hover:bg-custom-green-ccard-2 border-2 rounded-lg px-3 py-2"
            onClick={() => onApprove?.(row.original)}
            title="Aceptar / Rechazar"
          >
            <GoCheck /> Aceptar / Rechazar
          </button>
        </div>
      );
    },
    width: "190px",
  };

  return isPrivileged ? [...base, actionCol] : base;
};

const WithdrawalList = ({ data, onOpen, canManageWithdrawals, onViewDetail, approve, currentUserId }) => {
  const isPrivileged = canManageWithdrawals;
  const columns = useMemo(
    () => buildColumns({ onApprove: approve, isPrivileged, onViewDetail, currentUserId }),
    [approve, isPrivileged, onViewDetail, currentUserId]
  );

  return (
    <div className="relative flex flex-col gap-4 bg-slate-100">
      <div className="flex justify-between items-center">
        <h1 className="font-bold text-gray-700 text-3xl px-1">Retiro de equipos</h1>
        <button
          onClick={() => onOpen?.()}
          className="bg-custom-green-ccard-1 hover:bg-custom-green-ccard-2 font-bold text-white py-3 px-4 rounded-lg"
        >
          Retirar Equipo
        </button>
      </div>

      <Table columns={columns} data={Array.isArray(data) ? data : []} />
    </div>
  );
};

export default WithdrawalList;
